export const DynamicDataGroups = ["general", "cart", "customer", "product", "order", "smartGrid"] as const;
import { ActiveEntityType, DynamicDataContext, EnvironmentDataKeys, EnvironmentDataSubKeys } from "./activeEntityType";
export type DynamicDataGroup = (typeof DynamicDataGroups)[number];
export type DynamicDataSubGroup =
    | "User Info"
    | "Current Station Info"
    | "Outlet Info"
    | "Company Info"
    | "System Data"
    | "Cart Info"
    | "Customer Info"
    | "Product Info"
    | "Order Info"
    | "Billing Address"
    | "Shipping Address"
    | "Line Item Info"
    | "Action Info"
    | "Category Info"
    | "Folder Info";

export type DynamicDataInfoType = {
    label: string;
    info: string;
    group: DynamicDataGroup;
    subGroup: DynamicDataSubGroup;
    dataType: "string" | "number" | "currency" | "time" | "image" | "array";
    hide?: boolean; //do not show as an option in the popup
};

export const hubDynamicDataKey = "buildSettingsKey";

/// IMPORTANT NOTE:
// ALL ARRAY DATA TYPES ARE CURRENTLY CONVERGED TO STRINGS IN dynamicDataSpecialCases until we have a better way to handle them!
// meanwhile they will be hidden in the popup
export const DynamicData: Record<string, DynamicDataInfoType> = {
    // General
    [`${ActiveEntityType.USER}.firstName`]: {
        label: "First Name",
        info: "dynamicData.general.userFirstName.info",
        group: "general",
        subGroup: "User Info",
        dataType: "string"
    },
    [`${ActiveEntityType.USER}.lastName`]: {
        label: "Last Name",
        info: "dynamicData.general.userLastName.info",
        group: "general",
        subGroup: "User Info",
        dataType: "string"
    },
    [`${ActiveEntityType.USER}.fullName`]: {
        label: "Full Name",
        info: "dynamicData.general.userFullName.info",
        group: "general",
        subGroup: "User Info",
        dataType: "string"
    },
    [`${ActiveEntityType.USER}.email`]: {
        label: "Email",
        info: "dynamicData.general.userEmail.info",
        group: "general",
        subGroup: "User Info",
        dataType: "string"
    },
    [`${ActiveEntityType.USER}.phone`]: {
        label: "Phone",
        info: "dynamicData.general.userPhone.info",
        group: "general",
        subGroup: "User Info",
        dataType: "string"
    },
    [`${ActiveEntityType.USER}.role.name`]: {
        label: "Role",
        info: "dynamicData.general.userRole.info",
        group: "general",
        subGroup: "User Info",
        dataType: "string"
    },
    date: {
        label: "Date",
        info: "dynamicData.general.date.info",
        group: "general",
        subGroup: "Current Station Info",
        dataType: "time"
    },
    time: {
        label: "Time",
        info: "dynamicData.general.time.info",
        group: "general",
        subGroup: "Current Station Info",
        dataType: "time"
    },
    [`${ActiveEntityType.ACTIVE_STATION}.name`]: {
        label: "Station Name",
        info: "dynamicData.general.stationName.info",
        group: "general",
        subGroup: "Current Station Info",
        dataType: "string"
    },
    [`${ActiveEntityType.OUTLET}.address`]: {
        label: "Address 1",
        info: "dynamicData.general.outletAddress1.info",
        group: "general",
        subGroup: "Outlet Info",
        dataType: "string"
    },
    [`${ActiveEntityType.OUTLET}.address2`]: {
        label: "Address 2",
        info: "dynamicData.general.outletAddress2.info",
        group: "general",
        subGroup: "Outlet Info",
        dataType: "string"
    },
    [`${ActiveEntityType.OUTLET}.city`]: {
        label: "City",
        info: "dynamicData.general.outletCity.info",
        group: "general",
        subGroup: "Outlet Info",
        dataType: "string"
    },
    [`${ActiveEntityType.OUTLET}.state`]: {
        label: "State",
        info: "dynamicData.general.outletState.info",
        group: "general",
        subGroup: "Outlet Info",
        dataType: "string"
    },
    [`${ActiveEntityType.OUTLET}.postCode`]: {
        label: "Postal Code",
        info: "dynamicData.general.outletPostalCode.info",
        group: "general",
        subGroup: "Outlet Info",
        dataType: "string"
    },
    [`${ActiveEntityType.OUTLET}.name`]: {
        label: "Outlet Name",
        info: "dynamicData.general.outletName.info",
        group: "general",
        subGroup: "Outlet Info",
        dataType: "string"
    },
    [`${ActiveEntityType.OUTLET}.phone`]: {
        label: "Phone",
        info: "dynamicData.general.outletPhone.info",
        group: "general",
        subGroup: "Outlet Info",
        dataType: "string"
    },
    [`${ActiveEntityType.OUTLET}.fullAddress`]: {
        label: "Full Address",
        info: "dynamicData.general.outletFullAddress.info",
        group: "general",
        subGroup: "Outlet Info",
        dataType: "string"
    },
    [`${ActiveEntityType.COMPANY}.name`]: {
        label: "Company Name",
        info: "dynamicData.general.companyName.info",
        group: "general",
        subGroup: "Company Info",
        dataType: "string"
    },
    [`${ActiveEntityType.COMPANY}.settings.companyUrl`]: {
        label: "Company URL",
        info: "dynamicData.general.companyUrl.info",
        group: "general",
        subGroup: "Company Info",
        dataType: "string"
    },
    [`${ActiveEntityType.COMPANY}.logo`]: {
        label: "Company Logo",
        info: "dynamicData.general.companyLogo.info",
        group: "general",
        subGroup: "Company Info",
        dataType: "image"
    },
    [`${ActiveEntityType.COMPANY}.taxId`]: {
        label: "Tax ID",
        info: "dynamicData.general.companyTaxID.info",
        group: "general",
        subGroup: "Company Info",
        dataType: "string"
    },
    [`${ActiveEntityType.BUILD}.name`]: {
        label: "Build Name",
        info: "dynamicData.general.buildName.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${ActiveEntityType.BUILD}.version`]: {
        label: "Build Version",
        info: "dynamicData.general.buildVersion.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.NETWORK_INFO}.${EnvironmentDataSubKeys.DEVICE_STATUS}`]: {
        label: "Device Status",
        info: "dynamicData.general.deviceStatus.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.NETWORK_INFO}.${EnvironmentDataSubKeys.LAST_ONLINE_TIME}`]: {
        label: "Last Online Time",
        info: "dynamicData.general.lastOnlineTime.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.NETWORK_INFO}.${EnvironmentDataSubKeys.NETWORK_TYPE}`]: {
        label: "Network Type",
        info: "dynamicData.general.networkType.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.NETWORK_INFO}.${EnvironmentDataSubKeys.SIGNAL_STRENGTH}`]: {
        label: "Signal Strength",
        info: "dynamicData.general.signalStrength.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.NETWORK_INFO}.${EnvironmentDataSubKeys.NETWORK_NAME}`]: {
        label: "Network name",
        info: "dynamicData.general.networkName.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.NETWORK_INFO}.${EnvironmentDataSubKeys.IP_ADDRESS}`]: {
        label: "IP Address",
        info: "dynamicData.general.ipAddress.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DISPLAY}.${EnvironmentDataSubKeys.SCREEN_RESOLUTION}`]: {
        label: "Screen Resolution",
        info: "dynamicData.general.screenResolution.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DISPLAY}.${EnvironmentDataSubKeys.PIXEL_RATIO}`]: {
        label: "Pixel Ratio",
        info: "dynamicData.general.pixelRatio.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DISPLAY}.${EnvironmentDataSubKeys.ORIENTATION}`]: {
        label: "Orientation",
        info: "dynamicData.general.orientation.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.APP_INFO}.${EnvironmentDataSubKeys.APP_VERSION}`]: {
        label: "App Version",
        info: "dynamicData.general.appVersion.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.APP_INFO}.${EnvironmentDataSubKeys.BUILD_NUMBER}`]: {
        label: "Build Number",
        info: "dynamicData.general.buildNumber.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.APP_INFO}.${EnvironmentDataSubKeys.BUNDLE_ID}`]: {
        label: "Bundle ID",
        info: "dynamicData.general.bundleId.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.APP_INFO}.${EnvironmentDataSubKeys.INSTALLED}`]: {
        label: "Installed",
        info: "dynamicData.general.installed.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.APP_INFO}.${EnvironmentDataSubKeys.LAST_UPDATED}`]: {
        label: "Last Updated",
        info: "dynamicData.general.lastUpdated.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.LOCATION}.${EnvironmentDataSubKeys.LOCATION_ACCESS}`]: {
        label: "Location Access",
        info: "dynamicData.general.locationAccess.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.LOCATION}.${EnvironmentDataSubKeys.CURRENT_LOCATION}`]: {
        label: "Current Location",
        info: "dynamicData.general.currentLocation.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DEVICE_INFO}.${EnvironmentDataSubKeys.MODEL}`]: {
        label: "Model",
        info: "dynamicData.general.model.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DEVICE_INFO}.${EnvironmentDataSubKeys.SYSTEM_VERSION}`]: {
        label: "System Version",
        info: "dynamicData.general.systemVersion.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DEVICE_INFO}.${EnvironmentDataSubKeys.FIRMWARE_VERSION}`]: {
        label: "Firmware Version",
        info: "dynamicData.general.firmwareVersion.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DEVICE_INFO}.${EnvironmentDataSubKeys.SERIAL_NUMBER}`]: {
        label: "Serial Number",
        info: "dynamicData.general.serialNumber.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DEVICE_INFO}.${EnvironmentDataSubKeys.IMEI_NUMBER}`]: {
        label: "IMEI Number",
        info: "dynamicData.general.imeiNumber.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DEVICE_INFO}.${EnvironmentDataSubKeys.MAC_ADDRESS}`]: {
        label: "MAC Address",
        info: "dynamicData.general.macAddress.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DEVICE_INFO}.${EnvironmentDataSubKeys.ACTIVATION_TIME}`]: {
        label: "Activation Time",
        info: "dynamicData.general.activationTime.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DEVICE_INFO}.${EnvironmentDataSubKeys.TIME_ZONE}`]: {
        label: "Time Zone",
        info: "dynamicData.general.timeZone.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DEVICE_INFO}.${EnvironmentDataSubKeys.DEVICE_LANGUAGE}`]: {
        label: "Device Language",
        info: "dynamicData.general.deviceLanguage.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.SYSTEM_HEALTH}.${EnvironmentDataSubKeys.BATTERY_LEVEL}`]: {
        label: "Battery Level",
        info: "dynamicData.general.batteryLevel.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.SYSTEM_HEALTH}.${EnvironmentDataSubKeys.BATTERY_STATUS}`]: {
        label: "Battery Status",
        info: "dynamicData.general.batteryStatus.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.SYSTEM_HEALTH}.${EnvironmentDataSubKeys.LOW_POWER_MODE}`]: {
        label: "Low Power Mode",
        info: "dynamicData.general.lowPowerMode.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.SYSTEM_HEALTH}.${EnvironmentDataSubKeys.TOTAL_RAM}`]: {
        label: "Total RAM",
        info: "dynamicData.general.totalRam.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.SYSTEM_HEALTH}.${EnvironmentDataSubKeys.AVERAGE_CPU_USAGE}`]: {
        label: "Average CPU Usage",
        info: "dynamicData.general.averageCpuUsage.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.SYSTEM_HEALTH}.${EnvironmentDataSubKeys.CPU_SPEED}`]: {
        label: "CPU Speed",
        info: "dynamicData.general.cpuSpeed.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.SYSTEM_HEALTH}.${EnvironmentDataSubKeys.AVAILABLE_STORAGE}`]: {
        label: "Available Storage",
        info: "dynamicData.general.availableStorage.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.SYSTEM_HEALTH}.${EnvironmentDataSubKeys.TOTAL_STORAGE}`]: {
        label: "Total Storage",
        info: "dynamicData.general.totalStorage.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.SYSTEM_HEALTH}.${EnvironmentDataSubKeys.UPTIME}`]: {
        label: "Uptime",
        info: "dynamicData.general.uptime.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DEVELOPER}.${EnvironmentDataSubKeys.FLUTTER_VERSION}`]: {
        label: "Flutter Version",
        info: "dynamicData.general.flutterVersion.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DEVELOPER}.${EnvironmentDataSubKeys.DART_VERSION}`]: {
        label: "Dart Version",
        info: "dynamicData.general.dartVersion.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.DEVELOPER}.${EnvironmentDataSubKeys.ENVIRONMENT}`]: {
        label: "Environment",
        info: "dynamicData.general.environment.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    [`${EnvironmentDataKeys.PERIPHERAL_DEVICES}.${EnvironmentDataSubKeys.CONNECTED_PRINTER}`]: {
        label: "Connected Printer",
        info: "dynamicData.general.connectedPrinter.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },
    clockType: {
        label: "Clock Action type",
        info: "dynamicData.general.clockAction.info",
        group: "general",
        subGroup: "System Data",
        dataType: "string"
    },

    // Cart
    [`${ActiveEntityType.CART}.numberOfProductsInCart`]: {
        label: "Products In Cart",
        info: "dynamicData.cart.productsInCart.info",
        group: "cart",
        subGroup: "Cart Info",
        dataType: "number"
    },
    [`${ActiveEntityType.CART}.subtotal`]: {
        label: "Subtotal",
        info: "dynamicData.cart.subtotal.info",
        group: "cart",
        subGroup: "Cart Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.CART}.total`]: {
        label: "Total Balance",
        info: "dynamicData.cart.totalBalance.info",
        group: "cart",
        subGroup: "Cart Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.CART}.amountToBeCharged`]: {
        label: "Amount To Be Charged",
        info: "dynamicData.cart.amountToBeCharged.info",
        group: "cart",
        subGroup: "Cart Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.CART}.remainingBalance`]: {
        label: "Remaining Balance",
        info: "dynamicData.cart.remainingBalance.info",
        group: "cart",
        subGroup: "Cart Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.CART}.discount.amount`]: {
        label: "Total Discount Amount",
        info: "dynamicData.cart.totalDiscountAmount.info",
        group: "cart",
        subGroup: "Cart Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.CART}.customFee.amount`]: {
        label: "Cart Fees",
        info: "dynamicData.cart.cartFees.info",
        group: "cart",
        subGroup: "Cart Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.SPLIT_PAYMENT}.paidAmount`]: {
        label: "Paid Amount",
        info: "dynamicData.cart.paidAmount.info",
        group: "cart",
        subGroup: "Cart Info",
        dataType: "currency"
    },

    // Customer
    [`${ActiveEntityType.CUSTOMER}.firstName`]: {
        label: "Customer First Name",
        info: "dynamicData.customer.firstName.info",
        group: "customer",
        subGroup: "Customer Info",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.lastName`]: {
        label: "Customer Last Name",
        info: "dynamicData.customer.lastName.info",
        group: "customer",
        subGroup: "Customer Info",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.fullName`]: {
        label: "Customer Full Name",
        info: "dynamicData.customer.fullName.info",
        group: "customer",
        subGroup: "Customer Info",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.email`]: {
        label: "Customer Email",
        info: "dynamicData.customer.email.info",
        group: "customer",
        subGroup: "Customer Info",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.phone`]: {
        label: "Customer Phone",
        info: "dynamicData.customer.phone.info",
        group: "customer",
        subGroup: "Customer Info",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.billing.address1`]: {
        label: "Customer Billing Address 1",
        info: "dynamicData.customer.billingAddress1.info",
        group: "customer",
        subGroup: "Billing Address",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.billing.address2`]: {
        label: "Customer Billing Address 2",
        info: "dynamicData.customer.billingAddress2.info",
        group: "customer",
        subGroup: "Billing Address",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.billing.postCode`]: {
        label: "Customer Billing Postal Code",
        info: "dynamicData.customer.billingPostalCode.info",
        group: "customer",
        subGroup: "Billing Address",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.billing.city`]: {
        label: "Customer Billing City",
        info: "dynamicData.customer.billingCity.info",
        group: "customer",
        subGroup: "Billing Address",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.billing.country`]: {
        label: "Customer Billing Country",
        info: "dynamicData.customer.billingCountry.info",
        group: "customer",
        subGroup: "Billing Address",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.billing.state`]: {
        label: "Customer Billing State",
        info: "dynamicData.customer.billingState.info",
        group: "customer",
        subGroup: "Billing Address",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.shipping.address1`]: {
        label: "Customer Shipping Address 1",
        info: "dynamicData.customer.shippingAddress1.info",
        group: "customer",
        subGroup: "Shipping Address",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.shipping.address2`]: {
        label: "Customer Shipping Address 2",
        info: "dynamicData.customer.shippingAddress2.info",
        group: "customer",
        subGroup: "Shipping Address",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.shipping.postCode`]: {
        label: "Customer Shipping Postal Code",
        info: "dynamicData.customer.shippingPostalCode.info",
        group: "customer",
        subGroup: "Shipping Address",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.shipping.city`]: {
        label: "Customer Shipping City",
        info: "dynamicData.customer.shippingCity.info",
        group: "customer",
        subGroup: "Shipping Address",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.shipping.state`]: {
        label: "Customer Shipping State",
        info: "dynamicData.customer.shippingState.info",
        group: "customer",
        subGroup: "Shipping Address",
        dataType: "string"
    },
    [`${ActiveEntityType.CUSTOMER}.shipping.country`]: {
        label: "Customer Shipping Country",
        info: "dynamicData.customer.shippingCountry.info",
        group: "customer",
        subGroup: "Shipping Address",
        dataType: "string"
    },

    // Product
    [`${ActiveEntityType.PRODUCT}.name`]: {
        label: "Product Name",
        info: "dynamicData.product.productName.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "string"
    },
    [`${ActiveEntityType.PRODUCT}.stock`]: {
        label: "Stock",
        info: "dynamicData.product.stock.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "number"
    },
    [`${ActiveEntityType.PRODUCT}.sku`]: {
        label: "SKU",
        info: "dynamicData.product.sku.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "string"
    },
    [`${ActiveEntityType.PRODUCT}.price`]: {
        label: "Price",
        info: "dynamicData.product.price.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.PRODUCT}.quantity`]: {
        label: "Quantity",
        info: "dynamicData.product.quantity.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "number"
    },
    [`${ActiveEntityType.PRODUCT}.itemDiscount`]: {
        label: "Item Discount",
        info: "dynamicData.product.itemDiscount.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.PRODUCT}.shortDescription`]: {
        label: "Short Description",
        info: "dynamicData.product.shortDescription.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "string"
    },
    [`${ActiveEntityType.PRODUCT}.description`]: {
        label: "Long Description",
        info: "dynamicData.product.longDescription.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "string"
    },
    [`${ActiveEntityType.PRODUCT}.subtotal`]: {
        label: "Subtotal",
        info: "dynamicData.product.subtotal.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.PRODUCT}.fee.itemFee.amount`]: {
        label: "Product Fee",
        info: "dynamicData.product.fee.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.PRODUCT}.total`]: {
        label: "Total",
        info: "dynamicData.product.total.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.PRODUCT}.totalTax`]: {
        label: "Total Tax",
        info: "dynamicData.product.totalTax.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.PRODUCT}.source`]: {
        label: "Source",
        info: "dynamicData.product.source.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "string"
    },
    [`${ActiveEntityType.PRODUCT}.images`]: {
        label: "Product Image",
        info: "dynamicData.product.productImage.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "image"
    },
    [`${ActiveEntityType.PRODUCT}.customField`]: {
        label: "Custom Field",
        info: "dynamicData.product.customField.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "string"
    },
    [`${ActiveEntityType.PRODUCT}.attributes`]: {
        label: "Product Attributes",
        info: "dynamicData.product.attributes.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "array"
    },
    [`${ActiveEntityType.PRODUCT}.note`]: {
        label: "Product Note",
        info: "dynamicData.product.note.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "string"
    },

    // Order
    [`${ActiveEntityType.ORDER}.receiptId`]: {
        label: "Order Number",
        info: "dynamicData.order.number.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string"
    },
    [`${ActiveEntityType.ORDER}.createdAt`]: {
        label: "Order Date",
        info: "dynamicData.order.date.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "time"
    },
    [`${ActiveEntityType.ORDER}.status`]: {
        label: "Order Status",
        info: "dynamicData.order.status.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string"
    },
    [`${ActiveEntityType.ORDER}.summary.discountTotal`]: {
        label: "Order Discount Total",
        info: "dynamicData.order.discountTotal.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.ORDER}.summary.shippingTotal`]: {
        label: "Order Shipping Total",
        info: "dynamicData.order.shippingTotal.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.ORDER}.summary.subTotal`]: {
        label: "Order Subtotal",
        info: "dynamicData.order.subTotal.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.ORDER}.summary.total`]: {
        label: "Order Total",
        info: "dynamicData.order.total.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.ORDER}.summary.totalTaxes`]: {
        label: "Order Total Taxes",
        info: "dynamicData.order.totalTaxes.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.ORDER}.customerNote`]: {
        label: "Order Notes",
        info: "dynamicData.order.notes.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string"
    },
    [`${ActiveEntityType.ORDER}.balance`]: {
        label: "Order Balance",
        info: "dynamicData.order.balance.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "currency"
    },
    [`${ActiveEntityType.ORDER}.paymentMethods`]: {
        label: "Order Payment Methods",
        info: "dynamicData.order.paymentMethods.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "array",
        hide: true
    },

    // deprecated? all hidden, in current use are customer section - in the order context, customer data would get these details:
    [`${ActiveEntityType.ORDER}.billing.address1`]: {
        label: "Order Billing Address 1",
        info: "dynamicData.order.billing.address1.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string",
        hide: true
    },
    [`${ActiveEntityType.ORDER}.billing.address2`]: {
        label: "Order Billing Address 2",
        info: "dynamicData.order.billing.address2.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string",
        hide: true
    },
    [`${ActiveEntityType.ORDER}.billing.postCode`]: {
        label: "Order Billing Postal Code",
        info: "dynamicData.order.billing.postal.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string",
        hide: true
    },
    [`${ActiveEntityType.ORDER}.billing.city`]: {
        label: "Order Billing City",
        info: "dynamicData.order.billing.city.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string",
        hide: true
    },
    [`${ActiveEntityType.ORDER}.billing.country`]: {
        label: "Order Billing Country",
        info: "dynamicData.order.billing.country.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string",
        hide: true
    },
    [`${ActiveEntityType.ORDER}.billing.state`]: {
        label: "Order Billing State",
        info: "dynamicData.order.billing.state.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string",
        hide: true
    },
    [`${ActiveEntityType.ORDER}.shipping.address1`]: {
        label: "Order Shipping Address 1",
        info: "dynamicData.order.shipping.address1.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string",
        hide: true
    },
    [`${ActiveEntityType.ORDER}.shipping.address2`]: {
        label: "Order Shipping Address 2",
        info: "dynamicData.order.shipping.address2.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string",
        hide: true
    },
    [`${ActiveEntityType.ORDER}.shipping.postCode`]: {
        label: "Order Shipping Postal Code",
        info: "dynamicData.order.shipping.postal.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string",
        hide: true
    },
    [`${ActiveEntityType.ORDER}.shipping.city`]: {
        label: "Order Shipping City",
        info: "dynamicData.order.shipping.city.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string",
        hide: true
    },
    [`${ActiveEntityType.ORDER}.shipping.state`]: {
        label: "Order Shipping State",
        info: "dynamicData.order.shipping.state.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string",
        hide: true
    },
    [`${ActiveEntityType.ORDER}.source`]: {
        label: "Order Source",
        info: "dynamicData.order.source.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "string",
        hide: true
    },
    [`${ActiveEntityType.ORDER}.cartFees`]: {
        label: "Cart Fees",
        info: "dynamicData.order.cartFees.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "array"
    },
    [`${ActiveEntityType.ORDER}.totalFees`]: {
        label: "Total Fees",
        info: "dynamicData.order.totalFees.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "array"
    },
    [`${ActiveEntityType.ORDER}.payments`]: {
        label: "Tips",
        info: "dynamicData.order.payments.info",
        group: "order",
        subGroup: "Order Info",
        dataType: "array"
    },

    // deprecated: stuff with spaces in the key - should add new keys instead and handle them with "special cases" in the hook.
    [`${ActiveEntityType.USER}.firstName ${ActiveEntityType.USER}.lastName`]: {
        label: "Full Name",
        info: "dynamicData.general.userFullName.info",
        group: "general",
        subGroup: "User Info",
        dataType: "string",
        hide: true
    },
    [`${ActiveEntityType.OUTLET}.address, ${ActiveEntityType.OUTLET}.city, ${ActiveEntityType.OUTLET}.state, ${ActiveEntityType.OUTLET}.postCode`]:
        {
            label: "Full Address",
            info: "dynamicData.general.outletFullAddress.info",
            group: "general",
            subGroup: "Outlet Info",
            dataType: "string",
            hide: true
        },
    [`${ActiveEntityType.CUSTOMER}.firstName ${ActiveEntityType.CUSTOMER}.lastName`]: {
        label: "Customer Full Name",
        info: "dynamicData.customer.fullName.info",
        group: "customer",
        subGroup: "Customer Info",
        dataType: "string",
        hide: true
    },
    //Smart Grid
    [`smartGrid.action.name`]: {
        label: "Action Name",
        info: "dynamicData.smartGrid.actionName.info",
        group: "smartGrid",
        subGroup: "Action Info",
        dataType: "string"
    },
    [`smartGrid.category.name`]: {
        label: "Category Name",
        info: "dynamicData.smartGrid.categoryName.info",
        group: "smartGrid",
        subGroup: "Category Info",
        dataType: "string"
    },
    [`smartGrid.category.image`]: {
        label: "Category Image",
        info: "dynamicData.smartGrid.categoryImage.info",
        group: "smartGrid",
        subGroup: "Category Info",
        dataType: "image"
    },
    [`smartGrid.folder.name`]: {
        label: "Folder Name",
        info: "dynamicData.smartGrid.folderName.info",
        group: "smartGrid",
        subGroup: "Folder Info",
        dataType: "string"
    },
    // deprecated, will be mapped to "description"
    [`${ActiveEntityType.PRODUCT}.longDescription`]: {
        label: "Long Description",
        info: "dynamicData.product.longDescription.info",
        group: "product",
        subGroup: "Product Info",
        dataType: "string",
        hide: true
    }
} as const;

export type DynamicDataKey = keyof typeof DynamicData;

export const groupAllowedPerContext: Record<
    DynamicDataContext,
    { group: DynamicDataGroup; excludedSubGroups?: DynamicDataSubGroup[] }[]
> = {
    general: [{ group: "general" }, { group: "cart" }],
    [`${ActiveEntityType.ORDER}`]: [
        { group: "order" },
        { group: "general", excludedSubGroups: ["System Data"] },
        { group: "customer" }
    ],
    [`${ActiveEntityType.CART}`]: [{ group: "general" }, { group: "cart" }, { group: "customer" }], //in the future might use group cart
    [`${ActiveEntityType.CUSTOMER}`]: [
        { group: "customer" },
        { group: "general", excludedSubGroups: ["Current Station Info", "System Data", "Outlet Info", "User Info"] }
    ],
    [`${ActiveEntityType.PRODUCT}`]: [{ group: "product" }],
    [`smartGrid`]: [{ group: "smartGrid" }, { group: "product" }]
};

export function isKeyAllowedInContext(key: DynamicDataKey, context: DynamicDataContext): boolean {
    try {
        if (key.split(".")?.[0] === "buildSettingsKey") return true;
        const allowedGroups = groupAllowedPerContext[context];

        if (!allowedGroups) return false;
        return allowedGroups.some(
            group =>
                group.group === DynamicData[key].group &&
                (!group.excludedSubGroups || !group.excludedSubGroups.includes(DynamicData[key].subGroup))
        );
    } catch {
        // console.error("could not find data", key, "in the context of", context);
        return false;
    }
}

export function isGroupAllowedInContext(group: DynamicDataGroup, context: DynamicDataContext): boolean {
    const allowedGroups = groupAllowedPerContext[context];
    if (!allowedGroups) return false;
    return allowedGroups.some(g => g.group === group);
}

export function isSubGroupAllowedInContext(
    group: DynamicDataGroup,
    subGroup: DynamicDataSubGroup,
    context: DynamicDataContext
): boolean {
    const allowedGroups = groupAllowedPerContext[context];
    if (!allowedGroups) return true;
    return allowedGroups.some(g => g.group === group && (!g.excludedSubGroups || !g.excludedSubGroups.includes(subGroup)));
}
